import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '@/store';
import type { CharacterType } from '@/types/character';
import { BasicsSection } from '@/features/sections/BasicsSection';
import { VirtuesFlawsSection } from '@/features/sections/VirtuesFlawsSection';
import { AbilitiesSection } from '@/features/sections/AbilitiesSection';
import { MagusSection } from '@/features/sections/MagusSection';
import { SheetSection } from '@/features/sections/SheetSection';
import { ShareBar } from '@/features/share/ShareBar';
import { BudgetSummary } from '@/features/budget/BudgetSummary';
import { SectionEyebrow } from '@/components/ui/SectionEyebrow';

const VALID_TYPES: CharacterType[] = ['grog', 'companion', 'magus'];

type Tab = 'basics' | 'virtues' | 'abilities' | 'magus' | 'sheet';

const TABS: Array<{ id: Tab; label: string; magusOnly?: boolean }> = [
  { id: 'basics', label: 'Basics' },
  { id: 'virtues', label: 'Virtues & Flaws' },
  { id: 'abilities', label: 'Abilities' },
  { id: 'magus', label: 'Arts & Spells', magusOnly: true },
  { id: 'sheet', label: 'Sheet' },
];

const EYEBROWS: Record<CharacterType, string> = {
  grog: 'I · Grog Muster',
  companion: 'II · Companion Chronicle',
  magus: 'III · Magus Forgework',
};

export function CreatorPage() {
  const { type } = useParams<{ type: string }>();
  const character = useStore((s) => s.character);
  const newCharacter = useStore((s) => s.newCharacter);
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('basics');

  useEffect(() => {
    if (!type || !VALID_TYPES.includes(type as CharacterType)) {
      navigate('/', { replace: true });
      return;
    }
    if (!character || character.type !== type) {
      newCharacter(type as CharacterType);
    }
  }, [type, character, navigate, newCharacter]);

  const tabs = useMemo(
    () => TABS.filter((t) => (t.magusOnly ? character?.type === 'magus' : true)),
    [character?.type]
  );

  if (!character) return null;

  return (
    <div className="relative mx-auto max-w-6xl px-6 pt-28 pb-16">
      <header className="no-print mb-8">
        <SectionEyebrow start>{EYEBROWS[character.type]}</SectionEyebrow>
        <h1
          className="font-serif font-normal leading-tight text-parchment-50"
          style={{ fontSize: 'clamp(36px, 5vw, 60px)' }}
        >
          {character.name ? (
            <>
              <em className="italic text-gold-300">{character.name}</em>
            </>
          ) : (
            <span className="text-parchment-100/60">Unnamed</span>
          )}
          <span className="chip ml-3 align-middle">{character.type}</span>
        </h1>
      </header>

      <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
        <div>
          <div className="no-print mb-6 flex flex-wrap gap-0 border-b border-gold-400/20">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`-mb-px px-4 py-3 font-display text-[12px] uppercase tracking-[0.22em] transition-colors
                            border-b-2 ${
                              tab === t.id
                                ? 'border-gold-400 text-gold-300'
                                : 'border-transparent text-parchment-100/60 hover:text-gold-300'
                            }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'basics' && <BasicsSection />}
          {tab === 'virtues' && <VirtuesFlawsSection />}
          {tab === 'abilities' && <AbilitiesSection />}
          {tab === 'magus' && character.type === 'magus' && <MagusSection />}
          {tab === 'sheet' && <SheetSection />}
        </div>

        <aside className="no-print space-y-4 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
          <ShareBar />
          <BudgetSummary />
        </aside>
      </div>
    </div>
  );
}
